/**
 * ══ THE PRACTICE ESTIMATE — a capital bid that exists only inside a "Show me how" run ═══════════════
 *
 * The owner, 1 Oct 2026: "build the show me how" — walk a person through a WHOLE capital bid in the
 * new step layout (Job details → Scope → Labour → Schedule → Indirects → Price → Proposal → Signature
 * → Award → Project), on the real screens, as a practice run that leaves nothing behind.
 *
 * ── WHY IT LIVES HERE AND NOT IN THE DATABASE ────────────────────────────────────────────────────
 * The daily-report and sticker tours start from a real screen and make something new, so blocking
 * their writes (practiceMode.js) is the whole story. This tour cannot: what it teaches is how an
 * estimate that ALREADY HAS systems, crews, a schedule, a price and a proposal is worked through to an
 * award, and there is no real one that is safe to award on a practice run. The two server-side ways to
 * have one were considered and rejected for the reasons practiceMode.js's header gives:
 *
 *   • a seeded "demo" estimate with an is_practice flag — every board, pipeline total, hit rate, CRM
 *     opportunity, bid list, export and report that reads `bids` (44 queries in ~20 files today) would
 *     have to remember to leave it out, forever. The owner rejected exactly that on 9 Aug 2026;
 *   • a real estimate created and deleted around the run — the award alone writes a project, a capital
 *     project, a frozen baseline and a Capex job with a budget, and the tab closing is how a training
 *     session ends.
 *
 * So the estimate is answered INSIDE THE BROWSER for the length of the run: every read of it is a
 * practice read (practiceMode.registerPracticeRead) and every write to it lands in the small store
 * below, which the next read reflects — so a name typed on Job details is still the name on the head
 * after the save re-reads it, and the PDF made on Proposal is there on Signature. Nothing reaches the
 * server, nothing exists before the run, and nothing exists after it.
 *
 * ── WHY IT CAN NEVER SHADOW A REAL ESTIMATE ──────────────────────────────────────────────────────
 * Its id is NEGATIVE (`PRACTICE_BID_ID`). A capital estimate's id is `bids.id`, a positive serial, so
 * no real estimate can ever answer to it, and every matcher below keys on it (or, after the practice
 * award, on the practice job this store minted). Anything else goes out exactly as it always does.
 * Outside a run nothing here is consulted at all: a practice address opened after the run asks the
 * server, which answers that there is no such estimate — the honest answer.
 *
 * ── EVERY VALUE IS MARKED ────────────────────────────────────────────────────────────────────────
 * The customer is "Practice Customer (not real)", the contact address is on `.invalid` (a domain that
 * by RFC 2606 can never deliver), the job number is PRACTICE-1, and every id this store mints starts
 * `practice-` (practiceMode.isPracticeId). A screenshot of any of it cannot be mistaken for a bid.
 */
import {
  PRACTICE_ID_PREFIX, isPracticeRefusal, practiceRefusal, practiceRunNumber, registerPracticeQueryPurge,
  registerPracticeRead, registerPracticeResponse,
} from '../practiceMode'

export { isPracticeRefusal }

// ── the ids ──────────────────────────────────────────────────────────────────────────────────────

/** The registered tour that walks this estimate. CapexRoute shows the practice row only during it. */
export const CAPEX_TOUR_ID = 'capex-estimate'
/** Negative ON PURPOSE: a capital estimate's id is a positive serial, so no real one can be this. */
export const PRACTICE_BID_ID = -1001
/**
 * THE PRACTICE JOB'S ONE NUMBER, whatever anybody types (review of 17f785bc, F1). It used to be the
 * number typed on the award — and the award form offers the header's job number, so typing a REAL job's
 * number (DZ56) made the practice store answer DZ56's cost report, its periods and its budget for the
 * rest of the run, cached under DZ56's own key with live forecast and lock controls beside it. Built
 * from the practice prefix, so no real job number is ever it; what is typed is kept for show only.
 */
export const PRACTICE_JOB_NUMBER = `${PRACTICE_ID_PREFIX}1`
export const PRACTICE_PROJECT_ID = `${PRACTICE_ID_PREFIX}project`
export const PRACTICE_CAPITAL_PROJECT_ID = `${PRACTICE_ID_PREFIX}capital-project`
export const PRACTICE_JOB_ID = `${PRACTICE_ID_PREFIX}job`
/** The board row's own id, so the page can tell it from the real ones. */
export const PRACTICE_ROW_ID = `${PRACTICE_ID_PREFIX}estimate`

/** The estimate's pages, as the step routes name them. */
export const practiceEstimatePath = (segment = '') =>
  `/capex/estimates/${PRACTICE_BID_ID}${segment ? `/${segment}` : ''}`

// ── the figures ──────────────────────────────────────────────────────────────────────────────────

const round2 = (n) => Math.round(n * 100) / 100
const todayIso = () => new Date().toISOString().slice(0, 10)
const inDays = (n) => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}
const nowIso = () => new Date().toISOString()

/**
 * One small, believable job — a pump-station piping package — sized so every page has something on it
 * and nothing on it is a wall. The price is BUILT here from its parts rather than typed, so the
 * build-up adds up to the price and the by-system breakdown ties out exactly, the way the real engine
 * does: a practice estimate whose numbers did not reconcile would teach somebody to distrust the page.
 */
const SYSTEMS = [
  { id: 9001, code: 'P-100', name: 'Process piping', notes: 'Practice drawing P-101' },
  { id: 9002, code: 'P-200', name: 'Pipe supports', notes: '' },
]

const TAKEOFF = [
  { id: 9101, wbs_id: 9001, line_ref: 'T1', spec: '6" carbon steel pipe', qty: 400, unit: 'LF', hr_per_unit: 0.5 },
  { id: 9102, wbs_id: 9001, line_ref: 'T2', spec: '6" butt welds', qty: 60, unit: 'EA', hr_per_unit: 2.5 },
  { id: 9103, wbs_id: 9002, line_ref: 'T3', spec: 'Pipe supports, fabricated', qty: 40, unit: 'EA', hr_per_unit: 2 },
]

const CREW_MEMBERS = [
  { id: 9211, classification: 'Pipe Fitter', headcount: 2, base_rate: 38, burden_pct: 33, fee_pct: 8.5, markup_pct: 75.56, billable_rate: 96.27 },
  { id: 9212, classification: 'Welder', headcount: 1, base_rate: 40, burden_pct: 33, fee_pct: 8.5, markup_pct: 75.56, billable_rate: 101.34 },
  { id: 9213, classification: 'Helper', headcount: 1, base_rate: 22, burden_pct: 30, fee_pct: 0, markup_pct: 60, billable_rate: 45.76 },
]
const CREW_HEADCOUNT = CREW_MEMBERS.reduce((a, m) => a + m.headcount, 0)
const BLENDED_RATE = round2(CREW_MEMBERS.reduce((a, m) => a + m.headcount * m.billable_rate, 0) / CREW_HEADCOUNT)

const WORK_ITEMS = [
  { id: 9301, wbs_id: 9001, takeoff_id: 9101, description: 'Run 6" pipe', qty: 400, unit: 'LF', hr_per_unit: 0.5, material_cost: 18000, start_day: 1, duration_days: 10 },
  { id: 9302, wbs_id: 9001, takeoff_id: 9102, description: 'Weld 6" butt joints', qty: 60, unit: 'EA', hr_per_unit: 2.5, material_cost: 1200, start_day: 4, duration_days: 8 },
  { id: 9303, wbs_id: 9002, takeoff_id: 9103, description: 'Set pipe supports', qty: 40, unit: 'EA', hr_per_unit: 2, material_cost: 4000, start_day: 12, duration_days: 5 },
]
const MATERIAL_MARKUP = 0.1
const hoursOf = (w) => round2(w.qty * w.hr_per_unit)
const PRICED_HOURS = WORK_ITEMS.reduce((a, w) => a + hoursOf(w), 0)

/** The indirects, at sell. */
const SUPERVISION = { classification: 'General Foreman', basis: 'hours', hours: 80, ratio: null, rate: 85, cost_rate: 58, ot_multiplier: 1.5 }
const EQUIPMENT = { id: 9401, name: 'Welding rig', weekly_rate: 1400, hours: 3, quantity: 1, cost_rate: 900 }
const PER_DIEM = { day_rate: 60, employees: 4, days: 15 }
const OTHER_DIRECT = { label: 'Hydrotest water and disposal', amount: 1500 }

const TERMS = { contingency_pct: 3, bond_pct: 1 }

/**
 * The price, in the engine's order: direct cost, the indirects, a subtotal, contingency, then the bond
 * on everything above it. Cost is what the same lines cost US (the rate card before markup), so the
 * margin on the Job details tiles is a margin and not a guess.
 */
function priceOf() {
  const craft = round2(PRICED_HOURS * BLENDED_RATE)
  const materials = round2(WORK_ITEMS.reduce((a, w) => a + w.material_cost, 0) * (1 + MATERIAL_MARKUP))
  const supervision = round2(SUPERVISION.hours * SUPERVISION.rate)
  const equipment = round2(EQUIPMENT.weekly_rate * EQUIPMENT.hours * EQUIPMENT.quantity)
  const perDiem = round2(PER_DIEM.day_rate * PER_DIEM.employees * PER_DIEM.days)
  const other = OTHER_DIRECT.amount
  const subtotal = round2(craft + materials + supervision + equipment + perDiem + other)
  const contingency = round2(subtotal * (TERMS.contingency_pct / 100))
  const bond = round2((subtotal + contingency) * (TERMS.bond_pct / 100))
  const total = round2(subtotal + contingency + bond)

  const craftCost = round2(CREW_MEMBERS.reduce((a, m) => a + m.headcount * m.base_rate * (1 + m.burden_pct / 100), 0)
    / CREW_HEADCOUNT * PRICED_HOURS)
  const cost = round2(craftCost + WORK_ITEMS.reduce((a, w) => a + w.material_cost, 0)
    + SUPERVISION.hours * SUPERVISION.cost_rate + EQUIPMENT.cost_rate * EQUIPMENT.hours + perDiem + other + contingency)
  // THE BUDGET AN AWARD WOULD FREEZE differs from the cost above by the allowances counted at cost —
  // here, the per diem's handling. Small on purpose: the lesson is that the two are different figures.
  const budget = round2(cost + 450)
  return { craft, materials, supervision, equipment, perDiem, other, subtotal, contingency, bond, total, cost, budget }
}

const PRICE = priceOf()

/** The by-system breakdown, allocated by hours, with the rounding put on the last row so it ties out. */
function breakdownRows() {
  const indirect = round2(PRICE.supervision + PRICE.equipment + PRICE.perDiem + PRICE.other)
  const rows = SYSTEMS.map((s) => {
    const items = WORK_ITEMS.filter((w) => w.wbs_id === s.id)
    const hours = items.reduce((a, w) => a + hoursOf(w), 0)
    const takeoff = TAKEOFF.filter((t) => t.wbs_id === s.id).reduce((a, t) => a + t.qty * t.hr_per_unit, 0)
    const share = hours / PRICED_HOURS
    const labor = round2(hours * BLENDED_RATE)
    const material = round2(items.reduce((a, w) => a + w.material_cost, 0) * (1 + MATERIAL_MARKUP))
    const unitQty = TAKEOFF.find((t) => t.wbs_id === s.id).qty
    return {
      wbs_id: s.id, code: s.code, name: s.name, scope_state: 'base',
      craft_hours: hours, estimated_hours: round2(takeoff), labor_total: labor, labor_cost: null,
      material_sell: material, sub_sell: 0,
      indirect_alloc: round2(indirect * share), contingency_alloc: round2(PRICE.contingency * share),
      bond_alloc: round2(PRICE.bond * share), adjustment_alloc: 0, all_in_total: 0,
      unit_qty: unitQty, unit: TAKEOFF.find((t) => t.wbs_id === s.id).unit, unit_cost: null, withheld_fields: [],
    }
  })
  for (const r of rows) {
    r.all_in_total = round2(r.labor_total + r.material_sell + r.indirect_alloc + r.contingency_alloc + r.bond_alloc)
  }
  const drift = round2(PRICE.total - rows.reduce((a, r) => a + r.all_in_total, 0))
  rows[rows.length - 1].all_in_total = round2(rows[rows.length - 1].all_in_total + drift)
  for (const r of rows) r.unit_cost = round2(r.all_in_total / r.unit_qty)
  return rows
}

// ── the store ────────────────────────────────────────────────────────────────────────────────────

/**
 * What the run has done to the estimate. Only what a write can change lives here; everything else is
 * computed from the constants above on every read, so it cannot drift.
 */
export function initialPracticeState() {
  return {
    header: {
      job_name: 'Practice estimate (Show me how)',
      job_number: PRACTICE_JOB_NUMBER,
      client_name: 'Practice Customer (not real)',
      // LINKED, as an estimate's customer is picked from the CRM on Job details (C6): the award takes it and asks for
      // none. No real CRM customer has this id; nothing on practice reaches the CRM.
      client_id: -1,
      location_site: 'Anywhere, TX',
      estimate_basis: 'Practice drawings, rev A',
      submittal_due: inDays(14),
      status: 'Draft',
      contract_type: 'Lump Sum',
      created_at: nowIso(),
      updated_at: null,
      updated_by_name: null,
    },
    terms: { ...TERMS },
    sections: null,
    options: null,
    files: [],
    signing: {},
    award: null,
    job: null,
    versions: 0,
    edits: 0,
    // {take-off id: the pick a Save sent} - the line's library rate and where its figure came from. Kept so
    // the practice save echoes the pick back, as the server's does, and the page settles (drop 3 review).
    picks: {},
  }
}

let state = initialPracticeState()
/**
 * The practice run `state` belongs to. A new run starts from `initialPracticeState()` whatever the last one did
 * — whether or not any screen was mounted to reset it when that run ended (practiceRunNumber).
 */
let stateRun = practiceRunNumber()
const listeners = new Set()

/** The state of THIS run. */
const current = () => {
  const run = practiceRunNumber()
  if (run !== stateRun) {
    stateRun = run
    state = initialPracticeState()
  }
  return state
}

const commit = (next) => {
  state = next
  stateRun = practiceRunNumber()
  for (const fn of Array.from(listeners)) {
    try { fn() } catch { /* one listener must not stop another */ }
  }
}

/** React's useSyncExternalStore pair. */
export const subscribePracticeEstimate = (fn) => { listeners.add(fn); return () => { listeners.delete(fn) } }
export const practiceEstimateSnapshot = () => current()

/** Back to the estimate as a run starts on it. Tests, and anything that must not wait for the next run. */
export function resetPracticeEstimate() {
  commit(initialPracticeState())
}

/**
 * TEST SEAM: put the estimate at a point in a run (a PDF made, an award given). The anchor guard's
 * harness answers the pages' reads from this same store, so the board row CapexRoute draws from it
 * and the pages it reads agree — exactly as they do in a run.
 */
export function setPracticeEstimateState(next) {
  commit(next)
}

// ── who is reading: money is withheld the way the server withholds it (F5) ──────────────────────

/**
 * Whether the person on the practice estimate may see bid MONEY (`bid:view_financials`). The real
 * routes null cost, gross profit, margin and the rate card for anyone without it and say so in
 * `withheld_fields` (shared/auth/money.py, core/rate_card_money.py); a practice estimate that showed
 * them would teach a superintendent that an estimate shows him its margin, and show him figures his
 * role is refused everywhere else. CapexRoute, which knows the user, sets this as it renders — before
 * any page under it asks. Until it does, nothing is shown: the safe way round.
 */
let viewer = { seesMoney: false }
export function setPracticeViewer(next) {
  viewer = { seesMoney: !!(next && next.seesMoney) }
}
export const practiceViewer = () => viewer

/** The server's lists (capital_estimates._STANDING_COST_FIELDS, rate_card_money, capital_estimate_inputs_db). */
const STANDING_COST = ['est_total_cost', 'gross_profit', 'margin_pct', 'budget_at_award', 'gp_at_budget', 'gp_at_budget_pct']
const MEMBER_COST = ['base_rate', 'override_rate', 'cost_rate', 'burden_pct', 'fee_pct', 'markup_pct', 'burdened_rate']
const WORK_ITEM_COST = ['labor_cost', 'material_cost', 'mat_unit_cost', 'sub_cost', 'material_markup_pct', 'sub_markup_pct',
  'material_markup_amount', 'sub_markup_amount', 'freight_markup_amount', 'material_markup_pct_effective', 'sub_markup_pct_effective']
const COST_TOKENS = ['cost', 'margin', 'markup', 'burden', 'profit']
const costKeys = (o) => Object.keys(o).filter((k) => k !== 'withheld_fields' && COST_TOKENS.some((t) => k.toLowerCase().includes(t)))

/** Null `fields` on `o` unless the reader may see money, and record what was withheld — or that nothing was. */
function withhold(o, fields, sees) {
  if (!o || typeof o !== 'object') return o
  if (sees) {
    if (!Array.isArray(o.withheld_fields)) o.withheld_fields = []
    return o
  }
  const gone = fields.filter((f) => f in o)
  for (const f of gone) o[f] = null
  o.withheld_fields = [...new Set([...(o.withheld_fields || []), ...gone])]
  return o
}
const withholdRows = (rows, fields, sees) => { for (const r of rows || []) withhold(r, fields, sees); return rows }

/** A priced payload (the summary, and every write's echo of it), as `rate_card_money.redact_capital_money`. */
function pricedAsSeen(p, sees) {
  for (const crew of p.crews || []) {
    withhold(crew, ['blended_cost_rate'], sees)
    withholdRows(crew.members, MEMBER_COST, sees)
  }
  withholdRows(p.work_items, WORK_ITEM_COST, sees)
  if (p.cost_breakdown) withholdRows(p.cost_breakdown.rows, ['labor_cost'], sees)
  for (const name of ['summary', 'waterfall']) {
    const block = p[name]
    if (!block) continue
    withhold(block, costKeys(block), sees)
    for (const step of block.steps || []) {
      if (COST_TOKENS.some((t) => String(step.key || '').toLowerCase().includes(t))) withhold(step, ['amount'], sees)
    }
  }
  return p
}

function inputsAsSeen(i, sees) {
  withholdRows(i.supervision, ['cost_rate'], sees)
  withholdRows(i.equipment, ['cost_rate', 'lump_cost'], sees)
  withholdRows(i.materials, ['unit_cost', 'markup_pct'], sees)
  withholdRows(i.labor, ['base_rate', 'burden_pct', 'fee_pct', 'markup_pct', 'override_rate', 'cost_rate'], sees)
  withhold(i.readouts, ['equipment_uncosted'], sees)
  withhold(i, ['equipment_cost_factor'], sees)
  return i
}

// ── what each read answers ───────────────────────────────────────────────────────────────────────

const STAMP = { bid_id: PRACTICE_BID_ID, tenant_id: 'practice', created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z' }

function stageOf(s) {
  if (s.job) return 'won'
  if (s.award) return 'setup_pending'
  if (s.header.status === 'Submitted' || s.header.status === 'Sent') return 'submitted'
  return 'estimating'
}

const LOCKED = ['Submitted', 'Sent', 'Awarded']

export function shellOf(s = current()) {
  const locked = LOCKED.includes(s.header.status)
  return {
    bid_id: PRACTICE_BID_ID,
    stage: stageOf(s),
    note: 'This is a practice estimate for “Show me how”. It exists only while the practice run is on: nothing on it is saved, and nobody else can see it.',
    header: { ...s.header },
    award: s.award ? { ...s.award, job_number: s.job ? s.job.number : null, job_phase: s.job ? 'Awarded' : null } : null,
    standing: {
      price: PRICE.total,
      priced_hours: PRICED_HOURS,
      estimated_hours: PRICED_HOURS,
      estimated_hours_source: 'takeoff',
      estimated_hours_asserted: null,
      estimated_hours_takeoff: PRICED_HOURS,
      hours_coverage: 1,
      systems: SYSTEMS.length,
      work_items: WORK_ITEMS.length,
      crews: 1,
      takeoff_rows: TAKEOFF.length,
      supervision_rows: 1,
      equipment_lines: 1,
      material_lines: 0,
      labour_lines: 0,
      scheduled_items: WORK_ITEMS.length,
      unscheduled_items: 0,
      adjustments: 0,
      per_diem_set: true,
      proposal_sections: sectionsOf(s).sections.length,
      unit: 'LF',
      unit_qty: 400,
      ties_out: true,
      tie_out: 0,
      est_total_cost: PRICE.cost,
      gross_profit: round2(PRICE.total - PRICE.cost),
      margin_pct: round2(((PRICE.total - PRICE.cost) / PRICE.total) * 100),
      budget_at_award: PRICE.budget,
      gp_at_budget: round2(PRICE.total - PRICE.budget),
      gp_at_budget_pct: round2(((PRICE.total - PRICE.budget) / PRICE.total) * 100),
      withheld_fields: [],
    },
    attention: [],
    locked,
    lock_reason: locked ? `This estimate is ${s.header.status} and cannot be edited. Set it back to Draft first, or duplicate it.` : null,
  }
}

/** The take-off's rate-pick columns (2026_12_07), as the server's SELECT * returns them: empty unless picked. */
const PICK_COLUMNS = ['unit_rate_id', 'rate_basis', 'rate_n', 'rate_model_id', 'rate_model_version', 'rate_citation',
  'rate_picked_at', 'rate_picked_by', 'rate_hr_per_unit', 'attributes']
const NO_PICK = Object.fromEntries(PICK_COLUMNS.map((k) => [k, null]))

export function structureOf(s = current()) {
  const picks = (s && s.picks) || {}
  return {
    wbs: SYSTEMS.map((w, i) => ({ ...w, parent_id: null, sort_order: i, scope_state: 'base', ...STAMP })),
    takeoff: TAKEOFF.map((t, i) => ({
      ...t, basis: 'Practice drawings, rev A', drawing_refs: 'P-101', confidence_pct: null, hours_method: 'unit_rate',
      direct_hours: 0, productivity_factor: 1, notes: '', sort_order: i, ...STAMP, ...NO_PICK, ...(picks[t.id] || {}),
    })),
    crews: [{
      id: 9201, name: 'Pipe crew', labor_template_id: null, notes: '', sort_order: 0, withheld_fields: [], ...STAMP,
      members: CREW_MEMBERS.map((m, i) => ({
        id: m.id, crew_id: 9201, labor_rate_id: null, classification: m.classification, headcount: m.headcount,
        base_rate: m.base_rate, burden_pct: m.burden_pct, fee_pct: m.fee_pct, markup_pct: m.markup_pct,
        ot_multiplier: 1.5, override_rate: null, is_overridden: false, sort_order: i, withheld_fields: [], tenant_id: 'practice',
      })),
    }],
    work_items: WORK_ITEMS.map((w, i) => ({
      id: w.id, wbs_id: w.wbs_id, takeoff_id: w.takeoff_id, description: w.description, qty: w.qty, unit: w.unit,
      hours_method: 'unit_rate', hr_per_unit: w.hr_per_unit, direct_hours: 0, unit_rate_item_id: null, crew_id: 9201,
      crew_override_json: null, mat_unit_cost: null, material_cost: w.material_cost, material_markup_pct: MATERIAL_MARKUP * 100,
      freight_pct: null, sub_cost: 0, sub_markup_pct: null, sort_order: i, notes: '', start_day: w.start_day,
      duration_days: w.duration_days, predecessor: '', withheld_fields: [], ...STAMP,
    })),
    withheld_fields: [],
  }
}

function scheduleOf() {
  const last = Math.max(...WORK_ITEMS.map((w) => w.start_day + w.duration_days - 1))
  const daily = Array.from({ length: last }, (_, i) => {
    const day = i + 1
    return round2(WORK_ITEMS
      .filter((w) => day >= w.start_day && day < w.start_day + w.duration_days)
      .reduce((a, w) => a + hoursOf(w) / w.duration_days, 0))
  })
  const peak = Math.max(...daily)
  return {
    last_day: last, scheduled_items: WORK_ITEMS.length, unscheduled_items: 0, unscheduled_hours: 0,
    scheduled_hours: PRICED_HOURS, daily_hours: daily, peak_day: daily.indexOf(peak) + 1, peak_hours: peak,
    peak_headcount: CREW_HEADCOUNT,
  }
}

function waterfallSteps() {
  const step = (key, label, amount, isRule = false) => ({ key, label, amount, is_rule: isRule, visible: true, withheld_fields: [] })
  return [
    step('craft_labor', 'Craft labor (billed)', PRICE.craft),
    step('materials', 'Materials (with handling)', PRICE.materials),
    step('subcontract', 'Subcontract', 0),
    step('supervision', 'Supervision', PRICE.supervision),
    step('equipment', 'Equipment', PRICE.equipment),
    step('per_diem', 'Per diem', PRICE.perDiem),
    step('other_direct', 'Other direct costs', PRICE.other),
    step('subtotal', 'Subtotal', PRICE.subtotal, true),
    step('contingency', `Contingency (${TERMS.contingency_pct}%)`, PRICE.contingency),
    step('bond', `Bond (${TERMS.bond_pct}%)`, PRICE.bond),
    step('grand_total', 'Price', PRICE.total, true),
  ]
}

export function summaryOf() {
  const rows = breakdownRows()
  return {
    summary: { grand_total: PRICE.total, est_total_cost: PRICE.cost, withheld_fields: [] },
    waterfall: {
      steps: waterfallSteps(),
      priced_hours: PRICED_HOURS, estimated_hours: PRICED_HOURS, estimated_hours_takeoff: PRICED_HOURS,
      estimated_hours_asserted: null, estimated_hours_source: 'takeoff', hours_coverage: 1, craft_labor: PRICE.craft,
      adjustment_lines: [], other_direct_adjustment_lines: [], withheld_fields: [],
    },
    cost_breakdown: {
      rows, ties_out: true, tie_out: 0, primary_unit: 'LF', total_unit_qty: 400, total_hours: PRICED_HOURS,
      total_estimated_hours: PRICED_HOURS, indirect_pool: round2(PRICE.supervision + PRICE.equipment + PRICE.perDiem + PRICE.other),
    },
    crews: [{
      id: 9201, name: 'Pipe crew', total_headcount: CREW_HEADCOUNT, blended_rate: BLENDED_RATE, withheld_fields: [],
      members: CREW_MEMBERS.map((m) => ({ id: m.id, billable_rate: m.billable_rate })),
    }],
    work_items: WORK_ITEMS.map((w) => ({
      id: w.id, hours: hoursOf(w), labor_total: round2(hoursOf(w) * BLENDED_RATE), labor_unpriced: false,
      material_sell: round2(w.material_cost * (1 + MATERIAL_MARKUP)), sub_sell: 0, withheld_fields: [],
    })),
    schedule: scheduleOf(),
    is_capital: true,
  }
}

/** The page payload a write echoes back (PricedEcho), so a save re-prices the way the server's does. */
const pricedEcho = () => {
  const s = summaryOf()
  return { summary: s.summary, waterfall: s.waterfall, cost_breakdown: s.cost_breakdown, crews: s.crews, work_items: s.work_items }
}

export function termsOf(s = current()) {
  return {
    terms: {
      handling_markup_pct: null, freight_pct: null, escalation_pct: null, escalation_basis: null,
      contingency_pct: s.terms.contingency_pct ?? null, bond_pct: s.terms.bond_pct ?? null, discount_pct: null,
      other_direct_costs: null, primary_unit: 'LF', estimated_hours: null, roster_accepted_by: null,
      roster_accepted_note: null, roster_accepted_at: null, ...s.terms,
    },
    adjustments: [],
  }
}

export function inputsOf() {
  const workingDays = 15
  return {
    ot_pct: 0, perdiem_day_rate: PER_DIEM.day_rate, perdiem_employees: PER_DIEM.employees, perdiem_days_paid: PER_DIEM.days,
    small_tools_pct: null, scaffold_pct: null, travel_pct: null, onboarding_pct: null, consumables_pct: null,
    mob_demob_cost: null, employee_expenses: null, miscellaneous: null, scaffolding_fixed: null, equipment_cost_factor: null,
    duration_value: 3, duration_unit: 'Weeks', hrs_per_day: 10,
    supervision: [{
      ...SUPERVISION, implied_hours: SUPERVISION.hours, bill_rate: SUPERVISION.rate, sheet_rate: SUPERVISION.rate,
      amount: PRICE.supervision, on_rates: true, withheld_fields: [],
    }],
    other_direct_costs: [{ ...OTHER_DIRECT }],
    equipment: [{
      id: EQUIPMENT.id, wbs_id: null, equipment_id: null, name: EQUIPMENT.name, basis: 'rate', rate_unit: 'weekly',
      hourly_rate: null, weekly_rate: EQUIPMENT.weekly_rate, hours: EQUIPMENT.hours, quantity: EQUIPMENT.quantity,
      mob_cost: 0, cost_rate: EQUIPMENT.cost_rate, lump_amount: null, lump_cost: null, override_rate: null, shift: null,
      line_total: PRICE.equipment, cost_state: 'costed', withheld_fields: [],
    }],
    materials: [],
    labor: [],
    readouts: {
      crew_hour_base: PRICED_HOURS, working_days: workingDays, per_diem: PRICE.perDiem,
      steps: { supervision: PRICE.supervision, equipment: PRICE.equipment, materials: 0, per_diem: PRICE.perDiem, other_direct: PRICE.other },
      equipment_uncosted: 0, withheld_fields: [],
    },
    equipment_library: [],
    rate_classifications: CREW_MEMBERS.map((m) => m.classification),
    stored_fee_pct: null,
    withheld_fields: [],
  }
}

/** A practice rate sheet carrying the crew's three trades, so every person on the crew is "on the sheet". */
const PRACTICE_SHEET = { id: PRACTICE_BID_ID, name: 'Practice rates (not real)' }
const SHEET_TRADES = CREW_MEMBERS.map((m, i) => ({ id: PRACTICE_BID_ID - 1 - i, classification: m.classification }))
const ratesOf = () => ({
  rate_sheet: PRACTICE_SHEET,
  sheets: [{ ...PRACTICE_SHEET, is_locked: false }],
  snapshot: { classifications: CREW_MEMBERS.map((m) => m.classification), count: CREW_MEMBERS.length },
  sheet_trades: SHEET_TRADES,
})

const machinesOf = () => ({
  rule: '', settings_path: 'Admin > Settings, the box “Machine cost on estimates”', fleet_missing: false,
  types: [], changed: [], withheld_fields: [],
})

const prose = (text) => ({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }] })

export function sectionsOf(s = current()) {
  if (s.sections) return { sections: s.sections }
  return {
    sections: [
      { id: 'practice-section-1', ordinal: 0, kind: 'prose', title: null, body: prose('Thank you for the chance to price this work. This is a practice proposal: it is not sent to anybody.') },
      { id: 'practice-section-2', ordinal: 1, kind: 'pricing', title: null, body: null },
      { id: 'practice-section-3', ordinal: 2, kind: 'prose', title: 'Clarifications', body: prose('Night work and hydrotest water supply by others.') },
    ],
  }
}

export function optionsOf(s = current()) {
  return {
    show_cost_breakdown: null, show_manpower: null, show_equipment: null, show_header_breakdown: null,
    round_materials_up: null, manpower_text: null,
    // `.invalid` can never deliver (RFC 2606), so a practice "send" could not reach anybody even if it
    // somehow left the browser.
    contact_name: 'Practice Buyer', contact_email: 'buyer@practice.invalid',
    commercial_terms: null, clarifications: null, exclusions: null, taxes_note: null, payment_terms: null,
    defaults: { show_cost_breakdown: true, show_manpower: true, show_equipment: true, show_header_breakdown: false, round_materials_up: false },
    ...(s.options || {}),
  }
}

/** The drawing on Scope, and every proposal PDF this run has made. */
export function documentsOf(s = current()) {
  const drawing = {
    id: `${PRACTICE_ID_PREFIX}drawing`, document_type: 'Estimate Basis', version: 1, filename: 'Practice drawing P-101 rev A.pdf',
    content_type: 'application/pdf', size_bytes: 120000, created_by_name: 'Practice', created_at: '2026-01-01T00:00:00Z',
    include_in_proposal: false, download_url: null,
  }
  const pdf = (f) => f.content_type === 'application/pdf'
  const latest = s.files.filter(pdf).reduce((a, f) => Math.max(a, f.version), 0)
  return [
    drawing,
    ...s.files.map((f) => ({
      ...f,
      // Only the newest PDF is the offer; an older one is kept, marked replaced, as the real list does.
      superseded_by_version: pdf(f) && f.version < latest ? latest : null,
      signing_state: (s.signing[f.id] && s.signing[f.id].signing_state) || 'draft',
      client_link_issued: !!(s.signing[f.id] && s.signing[f.id].client_link_issued),
    })),
  ]
}

const BLANK_SIGNING = {
  signing_state: 'draft', company_signed_at: null, company_signed_by_name: null, client_signed_at: null,
  client_signer_name: null, declined_reason: null, client_link_issued: false, client_link_used: false,
  client_first_viewed_at: null, client_last_viewed_at: null, client_view_count: 0, awaiting: null,
  has_signed_copy: false, chain: [],
}

export const signingOf = (s, docId) => ({ ...BLANK_SIGNING, ...(s.signing[docId] || {}) })

// ── the project an award makes ───────────────────────────────────────────────────────────────────

const period = () => todayIso().slice(0, 7)

function jobHeaderOf(s) {
  return {
    id: PRACTICE_JOB_ID, number: s.job.number, name: s.header.job_name, client: s.header.client_name || '',
    site: s.header.location_site, contract_type: s.job.contract_type || 'Lump Sum', phase: 'Awarded',
    pm: null, superintendent: null, original_contract: PRICE.total, retainage_pct: 0.1, award_date: todayIso(),
    bid_estimate_id: null, summary_only: false, project_ref: null, budget_baseline_id: null,
    budget_baseline_version: 1, indirect_basis: s.job.indirect_basis || 'spread_by_hours',
  }
}

const TYPE_NAMES = { L: 'Labor', M: 'Material', S: 'Subcontract', E: 'Equipment', O: 'Other' }

/** The cost report of a job set up a minute ago: a budget per cost code, no cost, no forecast yet. */
function costReportOf(s) {
  const rows = breakdownRows()
  const ratio = PRICE.budget / PRICE.total
  const codeRows = rows.map((r) => {
    const budget = round2(r.all_in_total * ratio)
    const split = { L: round2(budget * 0.55), M: round2(budget * 0.3), E: round2(budget * 0.05), O: 0, S: 0 }
    split.O = round2(budget - split.L - split.M - split.E)
    const byType = Object.entries(split).filter(([, v]) => v !== 0).map(([k, v]) => ({
      cost_type: k, name: TYPE_NAMES[k], budget_orig: v, budget_co: 0, budget_cur: v, commit: 0, commit_rem: 0,
      cost: 0, ctc: 0, eac: 0, var: v, var_pct: 1, cost_pct: 0,
    }))
    return {
      code: r.code, description: r.name, vendor: null, time_based: false, crew_rate: BLENDED_RATE,
      budget_orig: budget, budget_co: 0, budget_cur: budget, commit: 0, commit_rem: 0, cost: 0, ctc: 0, eac: 0,
      var: budget, var_pct: 1, cost_pct: 0, budget_hours: r.craft_hours, act_hours: 0, fc_hours: r.craft_hours,
      earned_hours: 0, pf: null, phys: 0, by_type: byType,
    }
  })
  const budget = round2(codeRows.reduce((a, r) => a + r.budget_cur, 0))
  const totals = {
    budget_orig: budget, budget_co: 0, budget_cur: budget, cost: 0, commit: 0, commit_rem: 0, ctc: 0, eac: 0, var: budget,
    budget_hours: PRICED_HOURS, act_hours: 0, fc_hours: PRICED_HOURS, earned_hours: 0, pf: null,
    by_type: ['L', 'M', 'E', 'O'].map((k) => ({
      cost_type: k, name: TYPE_NAMES[k], budget_cur: round2(codeRows.reduce((a, r) => a + (r.by_type.find((t) => t.cost_type === k)?.budget_cur ?? 0), 0)), cost: 0, eac: 0, commit: 0,
    })),
    co_rev: 0, co_cost: 0, pending_rev: 0, pending_cost: 0, potential_rev: 0, potential_cost: 0, sub_co_cost: 0,
    original_contract: PRICE.total, contract: PRICE.total, bid_gp: round2(PRICE.total - budget),
    bid_gp_pct: (PRICE.total - budget) / PRICE.total, budget_gp: round2(PRICE.total - budget),
    proj_gp: null, proj_gp_pct: null, fade_pts: null, pct: null, earned: null, gp_to_date: null, billed: 0,
    over_under: null, over_under_pct: null, retainage: 0, ar_open: 0, collected: 0, cash_out: 0, backlog: null,
    gp_in_backlog: null, eac_pf: null, cred_ratio: null, slip_days: 0, retainage_overdue: false,
    rag: { margin: 'n', schedule: 'n', billing: 'n', productivity: 'n', credibility: 'n', overall: 'n' },
    no_budget: false, no_forecast: true, has_budget: true, has_forecast: false,
  }
  const job = jobHeaderOf(s)
  return {
    period: period(), locked: false, locked_through: null, open_period: period(), lockable: false,
    job,
    calc: {
      job_id: job.id, number: job.number, name: job.name, client: job.client, phase: job.phase, pm: null,
      decision: null, baseline_sc: null, forecast_sc: null, actual_sc: null, ar_days: 0, gp_history: [],
      rows: codeRows, totals,
    },
    walk: [], prior_eac: null,
    checks: [
      { label: 'Cost to date reconciles to the general ledger job cost', detail: 'not checked — this is a practice project', ok: false, state: 'not_checked' },
      { label: 'At-completion ≥ cost to date and ≥ commitment on every code', detail: `checked on ${codeRows.length} codes`, ok: true, state: 'ok' },
    ],
    held: [], held_amount: 0, family_overlaps: [],
  }
}

// ── the board row ────────────────────────────────────────────────────────────────────────────────

/** The practice estimate as the Pipeline lists it, while a practice run is on. */
export function practiceBoardRow(s = current()) {
  return {
    id: PRACTICE_ROW_ID,
    stage: stageOf(s),
    bid_id: PRACTICE_BID_ID,
    bid_status: s.header.status,
    capital_project_id: s.award ? PRACTICE_CAPITAL_PROJECT_ID : null,
    project_id: s.award ? PRACTICE_PROJECT_ID : null,
    name: s.header.job_name,
    number: s.header.job_number,
    client_name: s.header.client_name,
    value: PRICE.total,
    value_basis: s.award ? 'awarded' : 'bid',
    baseline_version: s.award ? 1 : null,
    awarded_at: s.award ? s.award.awarded_at : null,
    job_number: s.job ? s.job.number : null,
    job_phase: s.job ? 'Awarded' : null,
    note: null,
    last_moved: s.header.updated_at,
  }
}

// ── routing a request to the store ───────────────────────────────────────────────────────────────

const ID = String(PRACTICE_BID_ID)
/** `…/capital-estimates/-1001/inputs` → ['capital-estimates', 'inputs']. Matched on the TAIL of the
 *  path, so it reads the same through the app's `/api/bidworkup` prefix and the test transport's bare one. */
const ESTIMATE_PATH = new RegExp(`/(capital-estimates|capex|bids|proposal-doc)/${ID.replace('-', '\\-')}(?:/(.*))?$`)

function estimateRoute(path) {
  const m = ESTIMATE_PATH.exec(String(path || '').split('?')[0])
  return m ? { area: m[1], rest: m[2] || '' } : null
}

/**
 * The practice job, once the practice award has set one up — by ITS number or ITS id, and nothing
 * else. Never by what was typed: see PRACTICE_JOB_NUMBER for the real job that used to be answered here.
 */
function jobRoute(s, path) {
  if (!s.job) return null
  const clean = String(path || '').split('?')[0]
  const m = /\/jobs\/([^/]+)(?:\/(.*))?$/.exec(clean)
  if (!m) return null
  let id = m[1]
  try { id = decodeURIComponent(id) } catch { /* keep it as it came */ }
  if (id !== PRACTICE_JOB_NUMBER && id !== PRACTICE_JOB_ID) return null
  return { rest: m[2] || '' }
}

/** What the practice record says about what it does not do (F6, F7) — never a pretended success. */
const NOT_ON_PRACTICE = 'That is not available on the practice estimate, so nothing was done. On a real estimate this is where it would happen.'
const PRACTICE_JOB_ONLY = 'The practice project has only its cost report. A real project shows this page here.'
const NO_PRACTICE_FILE = 'The practice proposal is not a real file, so there is nothing to open. On a real estimate the PDF opens here.'

/** Is this the practice estimate (or its job)? */
export const isPracticeEstimatePath = (path, s = current()) => !!(estimateRoute(path) || jobRoute(s, path))

/**
 * The answer to a READ of the practice estimate, or `undefined` for anything that is not one — which
 * then goes out to the server exactly as it would have. Pure: `s` is the state to read.
 */
export function answerPracticeRead(s, method, path, who = viewer) {
  if (String(method).toUpperCase() !== 'GET') return undefined
  const sees = !!(who && who.seesMoney)
  const job = jobRoute(s, path)
  if (job) {
    if (job.rest === 'cost-report') return costReportOf(s)
    if (job.rest === 'periods') return { job_id: PRACTICE_JOB_ID, locked: [], locked_through: null, open_period: period(), current_period: period() }
    if (job.rest === 'ledger-cost') {
      return {
        job_id: PRACTICE_JOB_ID, project_ref: null, entries: 0, amount: 0, by_cost_type: {}, unknown_codes: [], held: [],
        held_amount: 0, uncoded: [], uncoded_amount: 0, uncategorised: [], skipped_zero: 0, project_ids: [],
      }
    }
    if (job.rest === 'budget/from-baseline') {
      return { status: 'already_loaded', message: 'The budget is loaded from estimate version 1.', source_bid_id: PRACTICE_BID_ID, indirect_basis: s.job.indirect_basis || 'spread_by_hours' }
    }
    // A new job has no change orders and no pay applications: those lists are honestly empty.
    if (job.rest === 'changes' || job.rest === 'pay-apps') return []
    // Every other page of the practice project says what it is rather than asking the server (F7).
    return practiceRefusal(PRACTICE_JOB_ONLY)
  }
  const r = estimateRoute(path)
  if (!r) return undefined
  const { area, rest } = r
  if (area === 'capital-estimates') {
    if (rest === '') {
      const shell = shellOf(s)
      withhold(shell.standing, STANDING_COST, sees)
      return shell
    }
    if (rest === 'inputs') return inputsAsSeen(inputsOf(s), sees)
    if (rest === 'rates') return ratesOf()
    if (rest === 'machines') return machinesOf()
  }
  if (area === 'capex') {
    if (rest === '') {
      const st = structureOf(s)
      for (const crew of st.crews) withholdRows(crew.members, MEMBER_COST, sees)
      withholdRows(st.work_items, WORK_ITEM_COST, sees)
      return st
    }
    if (rest === 'summary') return pricedAsSeen(summaryOf(s), sees)
    if (rest === 'terms') return termsOf(s)
    // THE BILL OF MATERIALS (C4, audit A4: the tour's step 3). The practice estimate buys nothing a vendor is
    // asked to price, so its bill is empty - said as a bill with no lines, never refused - and has no quotes.
    if (rest === 'bom') {
      return {
        lines: [], offers: [], categories: [], issues: [], estimate_locked: false,
        systems: SYSTEMS.map((w) => ({ id: w.id, label: w.name })),
        counts: { lines: 0, typed: 0, taken: 0, differs: 0, orphaned: 0, never_asked: 0, client_supplied: 0, offered: 0 },
      }
    }
    if (rest === 'bom/quotes') return { quotes: [] }
    const delta = /^scope-delta\/(\d+)$/.exec(rest)
    if (delta) {
      const row = breakdownRows().find((x) => String(x.wbs_id) === delta[1])
      if (row) {
        const stays = row.indirect_alloc
        const off = round2(row.all_in_total - stays)
        return {
          wbs_id: row.wbs_id, price_before: PRICE.total, price_after: round2(PRICE.total - off), price_delta: off,
          allocated_all_in: row.all_in_total, indirect_that_stays: stays,
        }
      }
    }
  }
  if (area === 'bids') {
    if (rest === 'documents') return documentsOf(s)
    const doc = /^documents\/([^/]+)\/(signing|consent)$/.exec(rest)
    if (doc && doc[2] === 'signing') return signingOf(s, doc[1])
    if (doc && doc[2] === 'consent') {
      return { consent_key: 'proposal_company_sign', consent_version: 'practice', consent_locale: 'en', text: 'Practice only — signing here signs nothing.' }
    }
    // The preview, a download, a signed copy: there is no file behind a practice proposal (F7).
    if (rest === 'export/pdf' || /^documents\/[^/]+\/(download|signed-copy)$/.test(rest)) return practiceRefusal(NO_PRACTICE_FILE)
  }
  if (area === 'proposal-doc') {
    if (rest === '') return sectionsOf(s)
    if (rest === 'options') return optionsOf(s)
  }
  // Anything else about the practice estimate is something it does not model — said, not sent.
  return practiceRefusal(NOT_ON_PRACTICE)
}

const withHeader = (s, patch) => ({
  ...s,
  header: { ...s.header, ...patch, updated_at: nowIso(), updated_by_name: 'you (practice)' },
  edits: s.edits + 1,
})

/**
 * The answer to a WRITE to the practice estimate, and the state it leaves — or `undefined` for a write
 * that is not to it (practice mode then answers it the generic way, as it always has). Pure.
 */
export function applyPracticeWrite(s, method, path, body, url = path, who = viewer) {
  const verb = String(method).toUpperCase()
  const b = body && typeof body === 'object' ? body : {}
  const sees = !!(who && who.seesMoney)
  const echo = () => pricedAsSeen(pricedEcho(), sees)
  const refused = { state: s, response: practiceRefusal(NOT_ON_PRACTICE) }

  // The practice job's own pages (a forecast, a lock, a change order): not modelled, and said so.
  if (jobRoute(s, path)) return refused

  // The SET-UP of the practice award's job: `POST /jobs` naming the practice project. Any other
  // project's set-up is not this store's business.
  if (verb === 'POST' && /\/jobs$/.test(String(path).split('?')[0])) {
    if (b.project_id !== PRACTICE_PROJECT_ID || !s.award) return undefined
    const next = { ...s, job: { number: PRACTICE_JOB_NUMBER, contract_type: s.award.contract_type, indirect_basis: b.indirect_basis || 'spread_by_hours' } }
    const header = jobHeaderOf(next)
    return {
      state: next,
      response: {
        ...header,
        budget: {
          status: 'loaded', message: null, original_budget: PRICE.budget, codes: SYSTEMS.length, baseline_version: 1,
          indirect_basis: next.job.indirect_basis, source_bid_id: PRACTICE_BID_ID,
        },
      },
    }
  }

  const r = estimateRoute(path)
  if (!r) return undefined
  const { area, rest } = r

  if (area === 'capital-estimates') {
    if (verb === 'PATCH' && rest === 'header') {
      const allowed = ['job_name', 'job_number', 'client_name', 'location_site', 'estimate_basis', 'submittal_due']
      const patch = Object.fromEntries(Object.entries(b).filter(([k]) => allowed.includes(k)))
      const next = withHeader(s, patch)
      return { state: next, response: { ...next.header } }
    }
    if (verb === 'POST' && rest === 'status' && typeof b.status === 'string') {
      const previous = s.header.status
      const next = withHeader(s, { status: b.status })
      const locked = LOCKED.includes(b.status)
      return {
        state: next,
        response: {
          bid_id: PRACTICE_BID_ID, status: b.status, previous_status: previous, changed: previous !== b.status,
          win_loss_reason: b.reason || null, locked, lock_reason: shellOf(next).lock_reason, notices: [],
        },
      }
    }
    if (verb === 'PATCH' && rest === 'inputs') {
      return { state: { ...s, edits: s.edits + 1 }, response: { ...inputsAsSeen(inputsOf(s), sees), ...echo() } }
    }
    if (verb === 'POST' && rest === 'rates/refresh') {
      // "Take the rate sheet's current rates": the practice sheet's rates ARE the crew's, so nothing moves.
      return {
        state: s,
        response: {
          ...echo(), price_before: PRICE.total, price_after: PRICE.total, rate_sheet: PRACTICE_SHEET,
          members_refreshed: CREW_MEMBERS.length, members_kept: 0, labour_lines_refreshed: 0, labour_lines_kept: 0,
          supervision_lines_repriced: 0, not_on_the_rate_sheet: [],
        },
      }
    }
    if (verb === 'POST' && rest === 'duplicate') {
      // A copy of a practice estimate is the practice estimate: there is nowhere real to put one.
      return { state: s, response: { bid_id: PRACTICE_BID_ID } }
    }
    return refused
  }

  if (area === 'capex') {
    if (verb === 'PUT' && rest === '') {
      // A pick on a practice line is KEPT and echoed - stamped picked now, as the server stamps a new pick - so
      // the page adopts it (`adoptSavedPicks`) and settles instead of showing the change as unsaved forever.
      // The practice price does not move: its figures are fixed, and a pick here is practice for the picker.
      const picks = { ...(s.picks || {}) }
      for (const row of Array.isArray(b.takeoff) ? b.takeoff : []) {
        if (!row || !TAKEOFF.some((t) => t.id === row.id)) continue
        if (row.unit_rate_id == null || row.unit_rate_id === '') {
          delete picks[row.id]
          continue
        }
        const hr = Number(row.hr_per_unit)
        picks[row.id] = {
          ...Object.fromEntries(PICK_COLUMNS.map((k) => [k, row[k] === undefined ? null : row[k]])),
          rate_picked_at: row.rate_picked_at || (picks[row.id] && picks[row.id].rate_picked_at) || nowIso(),
          rate_hr_per_unit: row.rate_hr_per_unit != null ? Number(row.rate_hr_per_unit) : hr,
          hr_per_unit: hr,
        }
      }
      const next = { ...s, picks, edits: s.edits + 1 }
      const st = structureOf(next)
      for (const crew of st.crews) withholdRows(crew.members, MEMBER_COST, sees)
      withholdRows(st.work_items, WORK_ITEM_COST, sees)
      return { state: next, response: { ok: true, ids: { refs: {}, dropped_references: [] }, capex: st, dropped_references: [], ...echo() } }
    }
    if (verb === 'PUT' && rest === 'terms') {
      const next = { ...s, terms: { ...s.terms, ...b }, edits: s.edits + 1 }
      return { state: next, response: { ok: true, ...termsOf(next), ...echo() } }
    }
    if (verb === 'POST' && rest === 'award') {
      // The practice project's number is the practice one, whatever the form sent (F1).
      const projectNumber = PRACTICE_JOB_NUMBER
      const awardedAt = nowIso()
      const next = withHeader({
        ...s,
        award: {
          capital_project_id: PRACTICE_CAPITAL_PROJECT_ID, project_id: PRACTICE_PROJECT_ID, project_number: projectNumber,
          baseline_version: 1, awarded_at: awardedAt, contract_type: b.contract_type || null,
        },
      }, { status: 'Awarded' })
      return {
        state: next,
        response: {
          ok: true, project_id: PRACTICE_PROJECT_ID, project_number: projectNumber, capital_project_id: PRACTICE_CAPITAL_PROJECT_ID,
          baseline_id: `${PRACTICE_ID_PREFIX}baseline`, award_id: `${PRACTICE_ID_PREFIX}award`, version: 1, baseline_reused: false,
          supersedes_id: null, total_price: PRICE.total, estimated_hours: PRICED_HOURS, priced_hours: PRICED_HOURS,
          hours_coverage: 1, contract_type: b.contract_type || null, contract_type_from_bid: s.header.contract_type,
          cost_codes: SYSTEMS.length, items: WORK_ITEMS.length, cost_split_frozen: true, activities: TAKEOFF.length,
          schedule_working_days: 15, completion_date: null, practice: true,
        },
      }
    }
    return refused
  }

  if (area === 'bids') {
    if (verb === 'POST' && rest === 'documents/generate') {
      // `fmt` rides in the query string ("Create a Word copy" asks for docx). A Word copy is a file, not
      // an offer: it is not signable, which is exactly how the real route files one.
      let fmt = 'pdf'
      try { fmt = new URL(String(url), 'http://practice.invalid/').searchParams.get('fmt') || 'pdf' } catch { /* pdf */ }
      const word = fmt === 'docx'
      const version = s.versions + 1
      const doc = {
        id: `${PRACTICE_ID_PREFIX}proposal-${version}`, document_type: 'Proposal', version,
        filename: `${s.header.job_name} - Proposal v${version} (PRACTICE).${word ? 'docx' : 'pdf'}`,
        content_type: word ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf',
        size_bytes: 48000, created_by_name: 'you (practice)', created_at: nowIso(), include_in_proposal: false,
        download_url: null, emailed: false, has_signed_copy: false, matches_estimate: true,
      }
      return {
        state: { ...s, versions: version, files: [...s.files, doc] },
        response: { id: doc.id, document_type: doc.document_type, version, filename: doc.filename, download_url: null, reused: false, voided: [] },
      }
    }
    const doc = /^documents\/([^/]+)\/(request-signature|reassign|withdraw|sign|send-to-client)$/.exec(rest)
    if (doc && verb === 'POST') {
      const [, docId, action] = doc
      const was = signingOf(s, docId)
      const when = nowIso()
      let now = was
      if (action === 'request-signature' || action === 'reassign') {
        const name = b.assignee_name || 'a colleague'
        now = {
          ...was, signing_state: 'awaiting_company',
          awaiting: { user_id: String(b.assignee_user_id || 'practice'), name, email: b.assignee_email || null, since: when, is_you: false },
          chain: [...was.chain, { seq: was.chain.length + 1, name, email: b.assignee_email || null, assigned_by: 'you (practice)', at: when, outcome: 'waiting', reason: b.reason || null }],
        }
      } else if (action === 'withdraw') {
        now = { ...was, signing_state: 'draft', awaiting: null }
      } else if (action === 'sign') {
        now = { ...was, signing_state: 'company_signed', awaiting: null, company_signed_at: when, company_signed_by_name: b.signed_name || 'you (practice)' }
      } else if (action === 'send-to-client') {
        now = { ...was, signing_state: 'sent', client_link_issued: true }
      }
      const next = { ...s, signing: { ...s.signing, [docId]: now } }
      const response = action === 'send-to-client'
        // `suppressed` is the real route's own answer when an environment does not deliver mail, and it
        // is the TRUE one here: nothing was mailed. Its sentence says why.
        ? { status: 'suppressed', to: b.to_email || optionsOf(s).contact_email, message: 'this is a practice run, so nothing was emailed', signing_state: 'sent' }
        : { status: 'ok', practice: true }
      return { state: action === 'send-to-client' ? withHeader(next, { status: 'Sent' }) : next, response }
    }
    return refused
  }

  if (area === 'proposal-doc') {
    if (verb === 'PUT' && rest === '' && Array.isArray(b.sections)) {
      const sections = b.sections.map((x, i) => ({ ...x, id: `${PRACTICE_ID_PREFIX}section-${i + 1}`, ordinal: i }))
      return { state: { ...s, sections, edits: s.edits + 1 }, response: { sections } }
    }
    if (verb === 'PATCH' && rest === 'options') {
      const next = { ...s, options: { ...(s.options || {}), ...b }, edits: s.edits + 1 }
      return { state: next, response: optionsOf(next) }
    }
    return refused
  }
  return refused
}

// ── wired into practice mode ─────────────────────────────────────────────────────────────────────

registerPracticeRead(
  (method, path) => isPracticeEstimatePath(path),
  ({ method, path }) => answerPracticeRead(current(), method, path),
)

registerPracticeResponse(
  (method, path) => isPracticeEstimatePath(path) || (method === 'POST' && /\/jobs$/.test(path)),
  ({ method, path, body, url }) => {
    const out = applyPracticeWrite(current(), method, path, body, url)
    if (!out) return undefined
    commit(out.state)
    return out.response
  },
)

// ── the query cache at both ends of a run (F2) ───────────────────────────────────────────────────

/** A cached query about the practice estimate or its job (Capex keys every one under 'capex'). */
export const isPracticeQueryKey = (key) => Array.isArray(key) && key[0] === 'capex'
  && key.some((k) => k === PRACTICE_BID_ID || k === PRACTICE_JOB_NUMBER || k === PRACTICE_JOB_ID)

registerPracticeQueryPurge((queryClient, { armed, late }) => {
  const practice = (q) => isPracticeQueryKey(q.queryKey)
  if (armed) {
    // A run starts from the estimate as it is: anything a last run cached about it goes — and a page
    // already open on it (a resume) reads it again, answered now by this run.
    void queryClient.resetQueries({ predicate: practice })
    return
  }
  // The run is over. Nothing of the practice estimate may stay cached — the page it ended on is still
  // mounted for one more render, so its queries go on the late pass, once that page has left.
  queryClient.removeQueries({ predicate: practice, type: 'inactive' })
  if (late) return
  // …and nothing a blocked write said about a REAL estimate either: an edit made on one during the run
  // was answered in the browser, and whatever cached that answer is dropped or read again.
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] === 'capex' && !practice(q), type: 'inactive' })
  void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'capex' && !practice(q) })
})

export default null
